/**
 * Shared types and constants for the ticketing API (ZPL-154 / ZPL-155).
 * Single source of truth for the request/response contract. A "defect"
 * ("usterka") is a ticket of kind='defect' — no second entity. Dates are
 * ISO strings (the house convention); there are no money fields.
 */

import type { AiActorTypeId, SystemActorType } from "./chat"
import type { UserSummaryDTO } from "./property-memberships"

export const TICKET_KINDS = ["task", "defect"] as const
export type TicketKind = (typeof TICKET_KINDS)[number]

export const TICKET_STATUSES = [
	"open",
	"in_progress",
	"done",
	"cancelled",
] as const
export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_LIMITS = {
	titleMax: 200,
	descriptionMax: 5000,
} as const

/** A ticket as returned by the API (JSON-serialized; Dates are ISO strings). */
export interface TicketDTO {
	id: string
	propertyId: string
	projectId: string | null
	topicId: string | null
	kind: TicketKind
	status: TicketStatus
	title: string
	description: string | null
	/** Resolved @-mentions on the description (ZPL-176); omitted when none. */
	descriptionMentions?: TicketMention[]
	/** Resolved assignee summary, or null when unassigned. */
	assignee: UserSummaryDTO | null
	dueAt: string | null
	reminderAt: string | null
	createdBy: string
	createdWithAi: boolean
	createdAt: string
	updatedAt: string
	/** Present once comments ship (S2); omitted in S1. */
	commentCount?: number
	/** Confirmed (ready) attachments on the ticket itself; present in S2. */
	attachments?: TicketAttachmentDTO[]
}

/** Request body for `POST /properties/:propertyId/tickets`. */
export interface CreateTicketBody {
	title: string
	/** Defaults to "task" server-side. */
	kind?: TicketKind
	/** Markdown; empty string clears (stored null). */
	description?: string
	/** Null/absent = property-level; set = project-scoped (must belong to the property). */
	projectId?: string | null
	topicId?: string | null
	/** Must be a current member of the property. Null/absent = unassigned. */
	assigneeUserId?: string | null
	/** ISO date-time string. */
	dueAt?: string | null
	reminderAt?: string | null
	/** Resolved @-mention picks for the description (ZPL-176); the server
	 *  validates each id against current Task.read, snapshots handles, drops
	 *  invalids/self. */
	mentionUserIds?: string[]
	/** Resolve `@mareczek.ai` on the description as an AI mention (not a user
	 *  recipient). */
	mentionsMareczek?: boolean
}

/**
 * Request body for `PATCH /properties/:propertyId/tickets/:ticketId`.
 * At least one field required. `status` is NOT here — it has its own
 * endpoint (`SetTicketStatusBody`) with transition-authority rules.
 * Empty string on `description` clears it.
 */
export interface UpdateTicketBody {
	title?: string
	description?: string
	kind?: TicketKind
	projectId?: string | null
	topicId?: string | null
	assigneeUserId?: string | null
	dueAt?: string | null
	reminderAt?: string | null
	/** Replace the description's resolved @-mentions (ZPL-176). Omitted =
	 *  preserve existing; present (even `[]` / `false`) = replace. */
	mentionUserIds?: string[]
	mentionsMareczek?: boolean
}

/** Request body for `POST /properties/:propertyId/tickets/:ticketId/status`. */
export interface SetTicketStatusBody {
	status: TicketStatus
}

export interface TicketResponse {
	ticket: TicketDTO
}

/**
 * `GET /properties/:propertyId/tickets?projectId=&status=&assigneeId=&topicId=&cursor=&limit=`
 * — newest-first, cursor-paginated like the property journal. `nextCursor` is
 * an opaque base64 token; null on the last page.
 */
export interface TicketListResponse {
	tickets: TicketDTO[]
	nextCursor: string | null
}

/**
 * A ticket as returned by `GET /users/me/tickets` — the base ticket plus the
 * owning property's and (when project-scoped) project's display names, so the
 * cross-property "My Tasks" list can label each row's scope without N extra
 * client-side lookups. `projectName` is null for property-level tasks. Kept
 * separate from `TicketDTO` so the property/project-scoped views (which already
 * know their property/project) aren't bloated. (ZPL-193)
 */
export interface MyTicketDTO extends TicketDTO {
	propertyName: string
	projectName: string | null
}

/**
 * `GET /users/me/tickets?propertyId=&projectId=&kind=&status=&cursor=&limit=`
 * — cross-property "My Tasks" ("Moje zadania"): tasks assigned to OR created by
 * the caller, newest-first, same cursor pagination. Rows are `MyTicketDTO` (they
 * carry the property/project names). ZPL-193 added the property/project/kind
 * filters and the name-enriched row.
 */
export interface MyTicketsResponse {
	tickets: MyTicketDTO[]
	nextCursor: string | null
}

export const TICKET_COMMENT_LIMITS = {
	bodyMax: 5000,
} as const

/** Attachment kinds (file-backed; ticket attachments have no URL kind). */
export const TICKET_ATTACHMENT_KINDS = ["image", "pdf", "video"] as const
export type TicketAttachmentKind = (typeof TICKET_ATTACHMENT_KINDS)[number]

/**
 * A confirmed (ready) attachment on a ticket or a ticket comment. Bytes live in
 * Cloudinary; `url` is a delivery URL, `thumbnailUrl` a derived transform.
 * Pending uploads are never surfaced.
 */
export interface TicketAttachmentDTO {
	id: string
	kind: TicketAttachmentKind
	url: string
	thumbnailUrl: string | null
	mimeType: string
	originalFilename: string | null
	/** The uploader's user id (null for non-user/system uploads) — lets the
	 *  client show uploader self-delete UI without exposing more than identity. */
	uploadedBy: string | null
}

/**
 * A resolved @-mention on a ticket description or comment (ZPL-176). Mirrors
 * chat's `ChatMention`: a `user` pick or the `mareczek-ai` AI mention. `handle`
 * is snapshotted at create/update so clients highlight the span without a live
 * lookup; the durable reference is `userId` (user) / `actorId` (AI). The
 * notification fan-out (ZPL-178) consumes only the `user` variant.
 */
export type TicketMention =
	| { type: "user"; userId: string; handle: string }
	| { type: "ai"; actorId: AiActorTypeId; handle: string }

/** A ticket comment as returned by the API (markdown `body`). */
export interface TicketCommentDTO {
	id: string
	ticketId: string
	actorType: SystemActorType
	actorId: string
	body: string
	/** Author summary for user-authored comments, or null for AI/deleted users. */
	author: UserSummaryDTO | null
	createdAt: string
	editedAt: string | null
	mentions?: TicketMention[]
	attachments?: TicketAttachmentDTO[]
}

/** Request body for `POST /properties/:propertyId/tickets/:ticketId/comments`. */
export interface CreateTicketCommentBody {
	body: string
	/** Resolved @-mention picks; the server validates each id is a current
	 *  Task.read holder on the ticket, snapshots handles, and drops invalids. */
	mentionUserIds?: string[]
	/** Resolve `@mareczek.ai` as an AI mention (triggers a Mareczek reply in
	 *  ZPL-177); not a user recipient. */
	mentionsMareczek?: boolean
}

/**
 * Request body for `PATCH .../tickets/:ticketId/comments/:commentId`
 * (author-only edit). `body` updates the text. Mention fields follow PATCH
 * semantics: omitted = preserve existing; present (even `[]` / `false`) =
 * replace via fresh server resolution.
 */
export interface UpdateTicketCommentBody {
	body?: string
	mentionUserIds?: string[]
	mentionsMareczek?: boolean
}

export interface TicketCommentListResponse {
	comments: TicketCommentDTO[]
}

/**
 * Request body for `POST .../tickets/:ticketId/attachments` (and the
 * `.../comments/:commentId/attachments` variant). File bytes never transit the
 * API — the server returns a signed Cloudinary direct-upload payload.
 */
export interface CreateTicketAttachmentBody {
	contentType: string
	filename: string
	declaredSize: number
}

/** The signed direct-upload payload; the client uploads to Cloudinary then confirms. */
export interface TicketAttachmentUploadResponse {
	attachmentId: string
	upload: { uploadUrl: string; params: Record<string, string> }
	expiresAt: string
}
